import { useMemo } from 'react';
import { Modal } from './Modal';

/**
 * Contextual in-app help — frontend-ui-system.md § In-app help system (binding:
 * "Every portal must include a built-in contextual help system", and "Every page must
 * have help content — if a page ships without help, it's a deliverable gap").
 *
 * Portal-agnostic on purpose. Each portal owns its own content (the rule requires it:
 * "Admin Portal and Client Portal have separate help content — different audiences,
 * different language") and passes it in. Only the resolution and rendering are shared,
 * so the two portals cannot drift on how a route maps to its help.
 *
 * Content is structured JSON. The rule permits "JSON or MDX"; JSON avoids adding a
 * markdown parser, which would be a new runtime dependency needing a tooling-principles
 * carveout for no benefit.
 *
 * Uses Modal rather than a bespoke drawer — the rule allows "slide-out drawer (right
 * side, 400px wide) or modal". Modal also carries the focus trap and focus restore
 * (#10803 AC6), so the panel inherits them rather than reimplementing them here.
 */

export interface HelpStep {
  task: string;
  steps: string[];
}

export interface HelpContent {
  title: string;
  summary: string;
  whatYouCanDo: string[];
  steps: HelpStep[];
  goodToKnow: string[];
}

export interface HelpIndexEntry {
  file: string;
  title: string;
}

export interface HelpIndex {
  routes: Record<string, HelpIndexEntry>;
}

/**
 * Resolve a pathname to its help file. Exact match first, then parameterised patterns
 * (`/board/:category`).
 *
 * The longest-first sort is DEFENSIVE, not load-bearing today: the exact-match pass
 * already claims a static path, and the pattern regex is anchored, so
 * `^/board/[^/]+$` cannot match `/board`. It matters only if two patterns of DIFFERENT
 * depth could ever match one path. Verified by falsification — reversing the sort
 * changes no current result, so no test asserts it.
 */
export function resolveHelpFile(index: HelpIndex, pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const routes = index.routes;
  if (routes[path]) return routes[path].file;

  const patterns = Object.keys(routes)
    .filter((p) => p.includes(':'))
    .sort((a, b) => b.split('/').length - a.split('/').length);

  for (const pattern of patterns) {
    const re = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$');
    if (re.test(path)) return routes[pattern].file;
  }
  return null;
}

/**
 * The structured-content renderer, on its own so the contextual panel and the Help
 * Centre (#10803 AC5) render an article identically.
 *
 * Extracted rather than copied: two renderers would drift, and a help system whose
 * search results look different from the panel that opened them teaches the reader
 * that they are two different things.
 */
export function HelpArticle({ content }: { content: HelpContent }) {
  return (
    <div className="flex flex-col gap-5 text-sm">
      <p className="text-[var(--text2)]">{content.summary}</p>

      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text3)]">
          What you can do here
        </h3>
        <ul className="flex flex-col gap-1.5 list-disc pl-5 text-[var(--text1)]">
          {content.whatYouCanDo.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {content.steps.map((group) => (
        <section key={group.task} className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text3)]">
            {group.task}
          </h3>
          <ol className="flex flex-col gap-1.5 list-decimal pl-5 text-[var(--text1)]">
            {group.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      ))}

      {content.goodToKnow.length > 0 && (
        <section className="flex flex-col gap-2 rounded-md bg-[var(--surface-2,var(--surface))] p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text3)]">
            Good to know
          </h3>
          <ul className="flex flex-col gap-1.5 list-disc pl-5 text-[var(--text2)]">
            {content.goodToKnow.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
  /** The portal's route -> content-file map. */
  index: HelpIndex;
  /** The portal's content, keyed by the filenames the index refers to. */
  content: Record<string, HelpContent>;
  /** Current pathname — passed in so this stays router-agnostic and directly testable. */
  pathname: string;
  /** Shown when a route has no help yet. Each portal words its own fallback. */
  emptyMessage?: string;
  /** Rendered under the empty message — e.g. a link into the Help Centre. */
  emptyAction?: React.ReactNode;
}

export function HelpPanel({
  open,
  onClose,
  index,
  content,
  pathname,
  emptyMessage = 'There is no help for this page yet.',
  emptyAction,
}: HelpPanelProps) {
  const entry = useMemo(() => {
    const file = resolveHelpFile(index, pathname);
    return file ? content[file] ?? null : null;
  }, [index, content, pathname]);

  // Honest empty state. A help button that opens a blank panel is the same defect as a
  // control that reports success and does nothing — say so instead of showing nothing.
  if (!entry) {
    return (
      <Modal open={open} onClose={onClose} title="Help" size="md">
        <p className="text-sm text-[var(--text2)]">{emptyMessage}</p>
        {emptyAction ? <div className="mt-4">{emptyAction}</div> : null}
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={entry.title} size="md">
      <HelpArticle content={entry} />
      {emptyAction ? <div className="mt-5 pt-4 border-t border-[var(--border)]">{emptyAction}</div> : null}
    </Modal>
  );
}
